// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

// Third party packages
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { default as moment, Moment } from 'moment';
import { ToastrService } from "ngx-toastr";
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, startWith, take, tap } from 'rxjs/operators';
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { User } from "src/app/shared/models/user/user.model";

// Core packages
import { MatDialog, MatOptionSelectionChange } from '@angular/material';
import { ViewPlatformNotesDialogComponent } from 'src/app/components/platform-note-card/components/view-platform-notes-dialog/view-platform-notes-dialog.component';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { DeadlinesService } from 'src/app/modules/deadlines/deadlines.service';
import { PropertiesService } from 'src/app/modules/properties/properties.service';
import { SubscriptionsService } from 'src/app/modules/subscriptions/subscriptions.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { ICompany } from "src/app/shared/models/company/company.interface";
import { PropertyInterface } from 'src/app/shared/models/property/property.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { DeadlinesDataSource } from 'src/app/shared/utils/data-sources/deadlines-data-source';
import { BalanceListDialogComponent } from '../../components/balances-list-dialog/balances-list-dialog.component';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-subscriptions-deadlines-list',
  templateUrl: './subscriptions-deadlines-list.page.html',
  styleUrls: ['./subscriptions-deadlines-list.page.scss'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class SubscriptionsDeadlinesListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];

  // BEGIN - Table deadlines
  displayedColumns: string[] = ['index', 'hasNotes', 'property', 'masterCode', 'endDate', 'totalDoc', 'subscription', 'forfait', 'options', 'adminCompany', 'actions'];
  dataSource: DeadlinesDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  propertyFilter: FormControl = new FormControl('');
  masterCodeFilter: FormControl = new FormControl('');
  subscriptionFilter: FormControl = new FormControl('');
  forfaitFilter: FormControl = new FormControl('');
  optionsFilter: FormControl = new FormControl('');
  endDateFilter: FormControl = new FormControl(moment().subtract(moment().date() - 1, 'days').startOf('day'));
  invoiceDateFilter: FormControl = new FormControl('');
  adminCompanyFilter: FormControl = new FormControl('');
  totalDocFilter: FormControl = new FormControl('');
  // createdAtFilter: FormControl = new FormControl('');
  // END - Table deadlines

  activeRoute: string;
  propertyIsLoading = false;
  availableProperties: PropertyInterface[] = [];
  availableStatus = [];
  roleType: string;

  selectedSubscriptions: any[] = [];

  roleAdmin = false;
  adminIsLoading = false;
  availableAdmins: User[] = [];

  // Distinct subscription keys used to populate the subscription column filter dropdown
  availableSubscriptions: { value: string; label: string }[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,

    private deadlinesService: DeadlinesService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private propertiesService: PropertiesService,
    private configService: ConfigService,
    private toastrService: ToastrService,
    private companiesService: CompaniesService,
    private subscriptionsService: SubscriptionsService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('deadlines.subscriptionsList.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));
    this.subscriptions.push(this.authService.loggedUserRequestedRoleType$.subscribe(res => {
      this.roleType = res;
      if (
        this.roleType === "master"
        || this.roleType === "collaborator"
      ) {
        this.displayedColumns.splice(this.displayedColumns.indexOf("forfait"), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf("options"), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf("actions"), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf("invoiceDate"), 1);
      }

      if (
        this.roleType === "admin"
        || this.roleType === "operator"
      ) {
        this.roleAdmin = true;
      }
    }));

    // Init data source
    this.dataSource = new DeadlinesDataSource(this.deadlinesService, this.helperService, this.translateService);

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings('deadlines');

    this.getAvailableAdmins();
    this.getAvailableSubscriptions();
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, 'keyup')
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.propertyFilter.valueChanges.pipe(startWith(null as string)),
        this.masterCodeFilter.valueChanges.pipe(startWith(null as string)),
        this.subscriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.forfaitFilter.valueChanges.pipe(startWith(null as string)),
        this.optionsFilter.valueChanges.pipe(startWith(null as string)),
        this.endDateFilter.valueChanges.pipe(startWith(null as string)),
        this.invoiceDateFilter.valueChanges.pipe(startWith(null as string)),
        this.adminCompanyFilter.valueChanges.pipe(startWith(null as string)),
        this.totalDocFilter.valueChanges.pipe(startWith(null as string)),
        // this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Handle property filter
          if (res) {
            this.propertyIsLoading = true;
            this.getAvailableProperties({
              general: this.propertyFilter.value
            });
            this.adminIsLoading = true;
            this.getAvailableAdmins({
              general: this.adminCompanyFilter.value
            });
          }


          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all deadlines
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'deadlines',
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        property: this.propertyFilter.value,
        masterCode: this.masterCodeFilter.value,
        subscription: this.subscriptionFilter.value,
        forfait: this.forfaitFilter.value,
        options: this.optionsFilter.value,
        endDate: this.endDateFilter.value,
        invoiceDate: this.invoiceDateFilter.value,
        adminCompany: this.adminCompanyFilter.value,
        totalDoc: this.totalDocFilter.value,
        // createdAt: this.createdAtFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      property: this.propertyFilter.value ? this.propertyFilter.value._id : undefined,
      masterCode: this.masterCodeFilter.value,
      subscription: this.subscriptionFilter.value,
      forfait: this.forfaitFilter.value,
      options: this.optionsFilter.value,
      endDate: this.endDateFilter.value ? new Date(this.endDateFilter.value).toISOString() : undefined,
      invoiceDate: this.invoiceDateFilter.value ? new Date(this.invoiceDateFilter.value).toISOString() : undefined,
      adminCompany: this.adminCompanyFilter.value ? this.adminCompanyFilter.value._id : undefined,
      totalDoc: this.totalDocFilter.value,
      // createdAt: this.createdAtFilter.value ? new Date(this.createdAtFilter.value).toISOString() : undefined,
    };
    this.dataSource.loadSubscriptionsItems(start, length, this.sort.active, this.sort.direction, search);
  }

  /**
   * Export deadlines in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('deadlines.subscriptionsList.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.deadlinesService.getSubscriptionsExport().subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('deadlines', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.propertyFilter.setValue(null);
    this.masterCodeFilter.setValue(null);
    this.subscriptionFilter.setValue(null);
    this.forfaitFilter.setValue(null);
    this.optionsFilter.setValue(null);
    this.endDateFilter.setValue(moment().subtract(moment().date() - 1, 'days').startOf('day'));
    this.invoiceDateFilter.setValue(moment().subtract(moment().date() - 1, 'days').startOf('day'));
    this.adminCompanyFilter.setValue(null);
    this.totalDocFilter.setValue(null);
    // this.createdAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "asc";
    this.sort.active = "endDate";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.general) {
                  // this.input.nativeElement.value = setting.search.general;
                }
                if (setting.search && setting.search.property) {
                  this.propertyFilter.setValue(setting.search.property);
                }
                if (setting.search && setting.search.masterCode) {
                  this.masterCodeFilter.setValue(setting.search.masterCode);
                }
                if (setting.search && setting.search.subscription) {
                  this.subscriptionFilter.setValue(setting.search.subscription);
                }
                if (setting.search && setting.search.forfait) {
                  this.forfaitFilter.setValue(setting.search.forfait);
                }
                if (setting.search && setting.search.options) {
                  this.optionsFilter.setValue(setting.search.options);
                }
                if (setting.search && setting.search.date) {
                  this.endDateFilter.setValue(setting.search.date);
                }
                if (setting.search && setting.search.date) {
                  this.invoiceDateFilter.setValue(setting.search.date);
                }
                if (setting.search && setting.search.adminCompany) {
                  this.adminCompanyFilter.setValue(setting.search.adminCompany);
                }
                if (setting.search && setting.search.totalDoc) {
                  this.totalDocFilter.setValue(setting.search.totalDoc);
                }
                // if (setting.search && setting.search.createdAt) {
                // this.createdAtFilter.setValue(setting.search.createdAt);
                // }
              }
            });
          }
        }
      )
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }


  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableProperties(search?: any): void {
    this.subscriptions.push(
      this.propertiesService
        .getList(0, null, null, null, search)
        .pipe(
          finalize(() => {
            this.propertyIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
            this.availableProperties = res.data.items;
          }
        })
    );
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayFn(arg?: PropertyInterface): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Handle click on a selected property in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onPropertyFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.propertyFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }


  /**
   * Handle click on "Open" button next to each item
   * and open a view dialog for clicked element
   *
   * @since 1.0.0
   */
  onOpenDialog(element: any): void {
    if (!element) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(BalanceListDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        element,
        propertyId: element._id,
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed()
      .subscribe(
        res => {
          if (res) {
            this.loadPage();
            return;
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }

  /**
   * Handle year selected from data filter
   *
   * @since 1.0.0
   */
  chosenYearHandler(normalizedYear: Moment): void {
    const ctrlValue = this.endDateFilter.value;
    ctrlValue.year(normalizedYear.year());
    this.endDateFilter.setValue(ctrlValue);
  }

  /**
   * Handle month selected from data filter
   *
   * @since 1.0.0
   */
  chosenMonthHandler(normalizedMonth: Moment, datepicker: MatDatepicker<Moment>): void {
    const ctrlValue = this.endDateFilter.value;
    ctrlValue.month(normalizedMonth.month());
    this.endDateFilter.setValue(ctrlValue);
    datepicker.close();
  }

  /**
   * Handle year selected from data filter
   *
   * @since 1.0.0
   */
   chosenYearInvoiceHandler(normalizedYear: Moment): void {
    const ctrlValue = this.invoiceDateFilter.value;
    ctrlValue.year(normalizedYear.year());
    this.invoiceDateFilter.setValue(ctrlValue);
  }

  /**
   * Handle month selected from data filter
   *
   * @since 1.0.0
   */
  chosenMonthInvoiceHandler(normalizedMonth: Moment, datepicker: MatDatepicker<Moment>): void {
    const ctrlValue = this.invoiceDateFilter.value;
    ctrlValue.month(normalizedMonth.month());
    this.invoiceDateFilter.setValue(ctrlValue);
    datepicker.close();
  }
  /**
   * Handle list of properties subscription to renew
   *
   * @since 1.0.0
   */
  onSubsSelected(subscription: any, checked: boolean): void {
    if (subscription && checked) {
      this.selectedSubscriptions.push(subscription._id);
    } else if (subscription && !checked) {
      const index = this.selectedSubscriptions.indexOf(subscription._id);
      this.selectedSubscriptions.splice(index, 1);
    }
  }

  /**
   * Renew the deadlines for propertys subscriptons
   *
   * @since 1.0.0
   */
  onRenewDeadlines(): void {
    if (!this.selectedSubscriptions.length) {
      this.toastrService.error(this.translateService.instant('common.errors.chooseElementFirst'));
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.renewSubscriptions.title'),
        message: this.translateService.instant('dialogs.renewSubscriptions.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          if (res2) {
            const data = {
              properties: this.selectedSubscriptions,
            };
            this.propertyIsLoading = true;
            this.subscriptions.push(
              this.deadlinesService.renewSubscriptionsDeadline(data)
                .subscribe(
                  res => {
                    if (res.status) {
                      this.propertyIsLoading = false;
                      this.toastrService.success(this.translateService.instant('deadlines.subscriptionsList.renewSuccessfully'));

                      this.selectedSubscriptions = [];
                      this.loadPage();
                    }
                  },
                  err => {
                    this.helperService.handleError(err);
                  }
                )
            );
          }
        }
      );
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayAdminFn(arg?: ICompany): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Handle click on a selected admin in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onAdminCompanyFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.adminCompanyFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }

  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableAdmins(search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getList(0, 10, null, null, search)
        .pipe(
          finalize(() => {
            this.adminIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
              this.availableAdmins = res.data.items;
          }
        })
    );
  }

  /**
   * Get the list of distinct subscription keys used to populate the
   * subscription column filter dropdown. The dropdown mirrors what the
   * column displays: the subscription key, or "SAG" for the
   * "noSubscription" key. The selected value is the raw subscription key,
   * which is exactly what the previous free-text input fed to the
   * back-end "subscription" search filter.
   *
   * @since 1.0.0
   */
  getAvailableSubscriptions(): void {
    this.subscriptions.push(
      this.subscriptionsService.getList().subscribe(
        res => {
          if (res.status && res.data && Array.isArray(res.data.items)) {
            const seenKeys: { [key: string]: boolean } = {};
            const keys: { value: string; label: string }[] = [];

            res.data.items.forEach((item: any) => {
              if (item && item.key && !seenKeys[item.key]) {
                seenKeys[item.key] = true;
                keys.push({
                  value: item.key,
                  label: item.key === 'noSubscription'
                    ? this.translateService.instant('subscriptions.fields.noSubscription')
                    : item.key,
                });
              }
            });

            // Always allow filtering the "SAG" (noSubscription) rows even
            // if the back-end catalog does not expose it as a dedicated item
            if (!seenKeys.noSubscription) {
              keys.push({
                value: 'noSubscription',
                label: this.translateService.instant('subscriptions.fields.noSubscription'),
              });
            }

            this.availableSubscriptions = keys;
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Open modal of property notes
   * 
   * @since 1.0.0
   */
  onOpenNotes(propertyId: string): void {
    this.dialog.open(ViewPlatformNotesDialogComponent, {
      data: {
        propertyId
      },
      width: '90%',
    });
  }

}
